import {
  Divider,
  IconButton,
  ListSubheader,
  MenuItem,
  TextField,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { Pill } from '@/designs-dev/inspect/Pill';
import { type StateKind } from '@/designs-dev/handover/types';
import { type Scene } from '@/designs-dev/scenes';

// Scenes grouped the way the handover page groups them, so the happy path reads
// first and the edge cases are visibly set apart.
const KIND_ORDER: StateKind[] = ['view', 'happy', 'empty', 'edge', 'error'];
const KIND_LABEL: Record<StateKind, string> = {
  view: 'Views',
  happy: 'Happy path',
  empty: 'Empty',
  edge: 'Edge cases',
  error: 'Errors',
};

// Which state of the prototype is on screen. Its own pill in the chrome above
// the frame, sitting to the right of DrillModeToggle and styled to match.
//
// It was a third segment inside that pill, which put two unlike things in one
// control: preview-vs-inspect and the frame size are how you are looking at the
// design, and the state is what the design is showing. Splitting them also means
// this one needs no inspect context at all — a stage with no scenes just doesn't
// render it, instead of passing three undefineds through the toggle.
//
// A chevron each side steps through the list in the order the menu shows it, so
// walking a flow does not mean opening the menu for every screen.
const StateSelector = ({
  scenes,
  sceneId,
  onSceneChange,
  sections,
}: {
  scenes: Scene<unknown>[];
  sceneId: string;
  onSceneChange: (id: string) => void;
  /**
   * Group by part of the journey rather than by kind, in this order. For a
   * prototype whose screens are steps: "happy path" says nothing when every
   * screen is on it, and the section a screen sits in is what you look for.
   */
  sections?: { id: string; label: string }[];
}) => {
  const groups =
    sections ?? KIND_ORDER.map((k) => ({ id: k, label: KIND_LABEL[k] }));

  const grouped = groups.map(({ id, label }) => ({
    id,
    label,
    scenes: scenes.filter((sc) =>
      sections ? sc.section === id : sc.kind === id,
    ),
  }));

  // The menu order, flattened: what the chevrons step through, so they move in
  // the order the list is read rather than the order the array happens to be in.
  const order = grouped.flatMap((g) => g.scenes.map((sc) => sc.id));
  const at = order.indexOf(sceneId);

  const step = (by: number) => {
    const next = order[at + by];
    if (next) onSceneChange(next);
  };

  return (
    // One pill: the chevrons sit inside it, either side of the value they step
    // through, so the whole thing reads as the one control for which state is on
    // screen rather than a control with two loose buttons beside it. A rule
    // between each chevron and the value keeps the three parts distinct, the way
    // DrillModeToggle separates its segments.
    <Pill spacing={0}>
      <IconButton
        aria-label='Previous state'
        size='small'
        disabled={at <= 0}
        onClick={() => step(-1)}
      >
        <Icon icon='chevron-left' fontSize={14} />
      </IconButton>

      <Divider orientation='vertical' flexItem />

      <TextField
        slotProps={{
          input: { disableUnderline: true },
          // Forty-odd screens long, and it used to open on top of the phone: you
          // lost sight of the design you were picking against at the moment you
          // were picking. Anchored to the pill's right edge it opens beside the
          // frame instead, and stops short of the viewport so the list scrolls
          // rather than the page.
          select: {
            MenuProps: {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              sx: { '& .MuiPaper-root': { ml: 1, maxHeight: '80vh' } },
            },
          },
        }}
        select
        size='small'
        variant='standard'
        value={sceneId}
        onChange={(e) => onSceneChange(e.target.value)}
        aria-label='Prototype state'
        // Fixed, not minimum: the pill sits above a phone and a longer state
        // name used to widen it, which moved the chevrons every time you
        // stepped. The value truncates instead.
        sx={{
          // Narrower on a phone, where three of these and the drill toggle
          // have to share one screen's width.
          width: { xs: 180, sm: 240 },
          px: 1,
          '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {grouped.flatMap((g) =>
          g.scenes.length === 0
            ? []
            : [
                <ListSubheader key={g.id}>{g.label}</ListSubheader>,
                ...g.scenes.map((sc) => (
                  <MenuItem key={sc.id} value={sc.id}>
                    {sc.label}
                  </MenuItem>
                )),
              ],
        )}
      </TextField>

      <Divider orientation='vertical' flexItem />

      <IconButton
        aria-label='Next state'
        size='small'
        disabled={at < 0 || at >= order.length - 1}
        onClick={() => step(1)}
      >
        <Icon icon='chevron-right' fontSize={14} />
      </IconButton>
    </Pill>
  );
};

export default StateSelector;
